import React from "react";
import {
  Container,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { HeadingGradient } from "../../Gradient";

// Shimmer animation
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// Vertical Book Cover Card Shimmer (matching HomeCarouselSection design)
const BookCoverCardShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const shimmerColor = isDark 
    ? "rgba(255,255,255,0.15)" 
    : "rgba(150,150,150,0.35)";
  const shimmerHighlight = isDark
    ? "rgba(255,255,255,0.25)"
    : "rgba(200,200,200,0.5)";

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: 120, sm: 150, md: 170 },
        display: "flex",
        flexDirection: "column",
        borderRadius: { xs: 1.5, md: 2 },
        backgroundColor: "transparent",
        overflow: "hidden",
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      {/* Cover Image Container - Matching actual card structure */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "3/4",
          minHeight: { xs: 160, sm: 200, md: 227 }, // Adjusted for 3:4 ratio
          maxHeight: { xs: 160, sm: 200, md: 227 },
          overflow: "hidden",
          borderRadius: { xs: 1.5, md: 2 },
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.03)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 12px rgba(0,0,0,0.3)"
              : "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        {/* Cover Image Placeholder with shimmer */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            background: `linear-gradient(90deg, ${shimmerColor} 25%, ${shimmerHighlight} 50%, ${shimmerColor} 75%)`,
            backgroundSize: "200% 100%",
            animation: `${shimmer} 2s linear infinite`,
          }}
        />
      </Box>

      {/* Content Placeholder - Matching actual card structure */}
        <Box
          sx={{
          pt: 1,
          pb: 0.5,
          display: "flex", 
          flexDirection: "column", 
          gap: 0.5 
        }}
      >
        {/* Title Placeholder - 2 lines matching actual card */}
        <Box
          sx={{
            height: { xs: 16, sm: 18, md: 20 },
            width: "90%",
            borderRadius: 0.5,
            background: shimmerColor,
            backgroundSize: "200% 100%",
            animation: `${shimmer} 2s linear infinite`,
          }}
        />
        <Box
          sx={{
            height: { xs: 16, sm: 18, md: 20 },
            width: "60%",
            borderRadius: 0.5,
            background: shimmerColor,
            backgroundSize: "200% 100%",
            animation: `${shimmer} 2s linear infinite`,
          }}
        />

        {/* Author Placeholder - "By [Author]" format matching actual card */}
        <Box
          sx={{
            height: { xs: 12, sm: 14 },
            width: "75%",
            borderRadius: 0.5,
            mt: { xs: 0.25, md: 0.5 },
            background: shimmerColor,
            backgroundSize: "200% 100%",
            animation: `${shimmer} 2s linear infinite`,
          }}
        />
      </Box>
    </Paper>
  );
};

// Carousel Section Shimmer (matching HomeCarouselSection structure)
const CarouselSectionShimmer = ({ title }: { title: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(480));
  const cardCount = 6; // Show 6 cards in shimmer (more than 5 to show navigation would appear)

  return (
    <Box sx={{ position: "relative", mb: { xs: 4, sm: 5, md: 8 } }}>
      {/* Section Title - Matching actual component */}
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <HeadingGradient
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
            mb: 0.5,
            opacity: 0.8,
          }}
        >
          {title}
        </HeadingGradient>
      </Box>

      {/* Carousel Container - Matching HomeCarouselSection layout */}
      <Box sx={{ position: "relative" }}>
        {/* Navigation Button Placeholder - Only show if more than 5 cards */}
        {cardCount > 5 && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: { xs: 4, sm: 6, md: 8 },
              transform: "translateY(-50%)",
              zIndex: 10,
              width: { xs: 32, sm: 36, md: 40 },
              height: { xs: 32, sm: 36, md: 40 },
              borderRadius: "50%",
              backdropFilter: "blur(12px)",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(255, 255, 255, 0.7)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
            }}
          />
        )}

        {/* Carousel Cards - Horizontal scrolling layout */}
          <Box
            sx={{
            display: "flex",
            gap: { xs: 1.5, md: 2 }, // Matching Swiper spaceBetween={16}
            overflowX: "hidden",
            paddingRight: cardCount > 5 ? (isMobile ? "48px" : "60px") : "8px",
            paddingLeft: isSmallScreen ? "4px" : "8px",
            position: "relative",
          }}
        >
          {Array.from({ length: cardCount }).map((_, index) => (
            <BookCoverCardShimmer key={index} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// HomePage Shimmer - Matching actual HomePage structure
const HomePageShimmer = () => {
  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: { xs: 4, sm: 6, md: 8 }, 
        px: { xs: 1.5, sm: 2, md: 3 } 
      }}
          >
      <Box>
        {/* Top picks for you */}
        <CarouselSectionShimmer title="Top picks for you" />
        
        {/* Top Stories */}
        <CarouselSectionShimmer title="Top Stories" />
        
        {/* New Releases */}
        <CarouselSectionShimmer title="New Releases" />
        
        {/* Category sections (showing a couple as examples) */}
        <CarouselSectionShimmer title="Top in Fan Fiction" />
        <CarouselSectionShimmer title="Top in Science Fiction" />
      </Box>
    </Container>
  );
};

export default HomePageShimmer;
